import { zonedTimeToUtc, format as fnsFormat } from 'date-fns-tz'
import { createContext, useCallback, useEffect, useState } from 'react'
import { getTimeZoneFormattedDate } from 'utils/common'
import { readTimezone, saveTimezone } from 'utils/timezoneStorage'
import { resolveTimezoneByTzCode } from 'utils/timezone'

const timeZoneInitial = {
  timeZone: '',
  timeZoneObject: null,
  setTimeZoneObject: null,
  updateTimezone: null,
  isInitialized: false,
  format: null,
  formatWithUTC: null,
}

export const TimeZoneContext = createContext(timeZoneInitial)

const useTimeZoneContext = (
  initialTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): TimeZoneContextObject => {
  const [timeZoneObject, setTimeZoneObject] = useState<TimeZone>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const timeZone = timeZoneObject?.tzCode ?? initialTimeZone

  // Hydrate timezone from sessionStorage on client mount
  // This runs once per context mount to restore persisted timezone
  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') {
      return
    }

    const storedTimezone = readTimezone()

    if (storedTimezone) {
      setTimeZoneObject(storedTimezone)
    }

    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (!isInitialized || !initialTimeZone || timeZoneObject) {
      return
    }

    setTimeZoneObject(resolveTimezoneByTzCode(initialTimeZone))
  }, [isInitialized, initialTimeZone, timeZoneObject])

  const updateTimezone = useCallback((timezone: TimeZone) => {
    setTimeZoneObject(timezone)
    saveTimezone(timezone)
  }, [])

  const formatWithUTC = (
    date: Date | string | number,
    dateFormat: string,
    formatTimeZone: string = timeZone
  ): string => {
    const utcTime = zonedTimeToUtc(date, formatTimeZone)
    return fnsFormat(utcTime, dateFormat)
  }

  // TODO: Date-fns-tz recently (v1.3.4 and up, dec-2021) introduced formatInZonedTime which is what the getTimeZoneFormattedDate helper function does ..
  // SO we can remove it and use formatInZonedTime
  const format = (
    date: Date | string | number,
    dateFormat: string,
    formatTimeZone: string = timeZone
  ): string => {
    if (!date) {
      return ''
    }

    return getTimeZoneFormattedDate(date, dateFormat, formatTimeZone)
  }

  return {
    timeZone,
    timeZoneObject,
    setTimeZoneObject,
    updateTimezone,
    isInitialized,
    format,
    formatWithUTC,
  }
}

export default useTimeZoneContext
